import React, { useState } from "react";
import { Box, Chip, IconButton, Paper, Typography } from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { PROGRAM_COLORS } from "../programLensConstants";
import ConditionCareNotes from "./ConditionCareNotes";
import { AssessmentSelectionDialog } from "./AssessmentsCard";

const C = PROGRAM_COLORS.BHI;

const BEHAVIORAL_TYPES = new Set([
  "phq9", "depression", "gad7", "anxiety", "sdoh", "cognitive",
  "substance_use", "trauma", "behavioral", "mental_health",
]);

function BhiSummaryCard({
  patientSummary,
  assessments,
  apiBase,
  patientId,
  onShowSnackbar,
  onStartAssessment,
  onAddBarrier,
  onEditBarrier,
  onAddGoal,
  onEditGoal,
  onAddCondition,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const allAssessments = assessments || [];
  const barriers = patientSummary?.barriers || [];
  const goals = patientSummary?.goals || [];
  const medications = patientSummary?.medications || [];

  // Filter to behavioral-relevant assessments
  const bhiAssessments = allAssessments.filter(
    (a) => BEHAVIORAL_TYPES.has(a.assessment_type) || (a.assessment_name || "").toLowerCase().includes("phq") || (a.assessment_name || "").toLowerCase().includes("gad") || (a.assessment_name || "").toLowerCase().includes("sdoh")
  );

  // Deduplicate: show only the latest per assessment_type
  const latestByType = {};
  bhiAssessments.forEach((a) => {
    const key = a.assessment_type || a.assessment_name;
    if (!latestByType[key] || new Date(a.completed_at) > new Date(latestByType[key].completed_at)) {
      latestByType[key] = a;
    }
  });
  const screenings = Object.values(latestByType);

  const riskColor = (level) => {
    if (level === "critical" || level === "high") return "#ef4444";
    if (level === "moderate" || level === "medium") return "#fbbf24";
    return "#4ade80";
  };

  const statusColor = (status) => {
    if (status === "achieved" || status === "resolved") return "#4ade80";
    if (status === "in_progress") return "#fbbf24";
    return "#888";
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <Paper
      sx={{
        border: `2px solid ${C.main}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: `${C.main}06`,
      }}
    >
      <Box
        sx={{
          bgcolor: `${C.main}18`,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: `1px solid ${C.main}25`,
        }}
      >
        <PsychologyIcon sx={{ fontSize: 18, color: C.main }} />
        <Typography sx={{ color: C.main, fontWeight: 700, fontSize: "0.85rem", letterSpacing: 0.5 }}>
          BHI SUMMARY
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: "0.75rem", ml: "auto" }}>
          Behavioral Health Integration
        </Typography>
      </Box>

      <Box sx={{ p: 2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        {/* Screenings */}
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
            <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}>
              SCREENINGS
            </Typography>
            {onStartAssessment && (
              <Chip
                icon={<AddIcon sx={{ fontSize: 12 }} />}
                label="New Screening"
                size="small"
                onClick={() => setPickerOpen(true)}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.65rem", color: C.main, borderColor: C.main, "& .MuiChip-icon": { color: C.main } }}
              />
            )}
          </Box>
          {screenings.length === 0 && (
            <Typography variant="caption" color="text.secondary">No behavioral screenings completed</Typography>
          )}
          {screenings.map((a) => (
            <Box
              key={a.id}
              sx={{ px: 1, py: 0.75, bgcolor: `${C.main}08`, borderRadius: 1, mb: 0.5 }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: "0.8rem" }}>{a.assessment_name || a.assessment_type}</Typography>
                <Chip
                  label={a.risk_level || "low"}
                  size="small"
                  sx={{ height: 18, fontSize: "0.6rem", bgcolor: `${riskColor(a.risk_level)}20`, color: riskColor(a.risk_level) }}
                />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.25}>
                <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>
                  Score: {a.total_score}/{a.max_score} — {fmtDate(a.completed_at)}
                </Typography>
                {onStartAssessment && (
                  <Typography
                    component="button"
                    type="button"
                    aria-label="Re-screen"
                    onClick={() => onStartAssessment(a.assessment_type)}
                    sx={{ fontSize: "0.68rem", color: C.main, cursor: "pointer", textDecoration: "underline", "&:hover": { opacity: 0.8 }, background: "none", border: 0, p: 0, fontFamily: "inherit" }}
                  >
                    Re-screen
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Conditions with care notes */}
        <ConditionCareNotes
          problems={patientSummary?.problems}
          patientId={patientId}
          apiBase={apiBase}
          accentColor={C}
          onShowSnackbar={onShowSnackbar}
          onAddCondition={onAddCondition}
        />

        {/* Barriers */}
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
            <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}>
              BARRIERS
            </Typography>
            {onAddBarrier && (
              <Chip
                icon={<AddIcon sx={{ fontSize: 12 }} />}
                label="Add"
                size="small"
                onClick={onAddBarrier}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.65rem", color: C.main, borderColor: C.main, "& .MuiChip-icon": { color: C.main } }}
              />
            )}
          </Box>
          {barriers.length === 0 && (
            <Typography variant="caption" color="text.secondary">No barriers</Typography>
          )}
          {barriers.map((b) => (
            <Box
              key={b.id}
              sx={{ px: 1, py: 0.5, bgcolor: `${C.main}08`, borderRadius: 1, mb: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Typography sx={{ flex: 1, fontSize: "0.8rem" }}>
                {b.barrier_type || b.type} — {b.description}
              </Typography>
              <Chip label={b.status} size="small" sx={{ height: 18, fontSize: "0.6rem", bgcolor: `${statusColor(b.status)}20`, color: statusColor(b.status) }} />
              {onEditBarrier && (
                <IconButton size="small" aria-label="Edit barrier" onClick={() => onEditBarrier(b)} sx={{ p: 0.25 }}>
                  <EditIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>

        {/* Behavioral Meds + Goals */}
        <Box>
          <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5, mb: 0.75 }}>
            BEHAVIORAL MEDICATIONS
          </Typography>
          {medications.length === 0 && (
            <Typography variant="caption" color="text.secondary">No medications</Typography>
          )}
          {medications.slice(0, 5).map((m, i) => (
            <Typography key={i} sx={{ fontSize: "0.8rem" }}>
              {m.drug || m.name}{" "}
              <Box component="span" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                {m.dose} {m.frequency}
              </Box>
            </Typography>
          ))}

          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1.5} mb={0.75}>
            <Typography sx={{ color: C.main, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}>
              GOALS
            </Typography>
            {onAddGoal && (
              <Chip
                icon={<AddIcon sx={{ fontSize: 12 }} />}
                label="Add"
                size="small"
                onClick={onAddGoal}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.65rem", color: C.main, borderColor: C.main, "& .MuiChip-icon": { color: C.main } }}
              />
            )}
          </Box>
          {goals.length === 0 && (
            <Typography variant="caption" color="text.secondary">No goals</Typography>
          )}
          {goals.map((g) => (
            <Box
              key={g.id}
              sx={{ px: 1, py: 0.5, bgcolor: `${C.main}08`, borderRadius: 1, mb: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Typography sx={{ flex: 1, fontSize: "0.8rem" }}>{g.description || g.goal_description}</Typography>
              <Chip label={g.status} size="small" sx={{ height: 18, fontSize: "0.6rem", bgcolor: `${statusColor(g.status)}20`, color: statusColor(g.status) }} />
              {onEditGoal && (
                <IconButton size="small" aria-label="Edit goal" onClick={() => onEditGoal(g)} sx={{ p: 0.25 }}>
                  <EditIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      </Box>
      <AssessmentSelectionDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelectAssessment={(type) => {
          setPickerOpen(false);
          onStartAssessment(type);
        }}
      />
    </Paper>
  );
}

export default BhiSummaryCard;
